import BlackButton from '@components/button/BlackButton'
import BasicLayout from '@components/layout/BasicLayout'
import Column from '@components/layout/Column'
import Container from '@components/layout/Container'
import FullScreenSection from '@components/layout/FullScreenSection'
import Row from '@components/layout/Row'
import PageSeo from '@components/seo/PageSeo'
import FullScreenVideo from '@components/video/FullScreenVideo'
import getDynasties from '@operations/queries/getDynasties'
import getSiteSettings from '@operations/queries/getSiteSettings'
import useMediaQuery, { maxWidthQueries, minWidthQueries } from '@operations/utils/useMediaQuery'
import Link from 'next/link'
import React, { useEffect, useRef } from 'react'
import ProgressiveImage from 'react-progressive-image'
import styled from 'styled-components'

// #region  styles
const SectionDivider = styled.img`
  position: absolute;
  width: 250%;
  bottom: -35px;
  @media ${minWidthQueries.md} {
    width: 100%;
    bottom: -25px;
  }
  @media ${minWidthQueries.lg} {
    bottom: -55px;
  }
`
const DynastySectionMediaWrap = styled(Column)`
  order: ${({ order }) => order};
`
const DynastySectionMedia = styled.img`
  max-height: 100%;
  max-width: 100%;
  width: 100%;
`
const DynastySectionBioWrap = styled(Column)`
  order: ${({ order }) => order};
`
const DynastySectionBioTitle = styled.h2`
  @media ${minWidthQueries.md} {
    font-size: ${({ theme }) => theme.font.fontSize.xxLarge};
  }
  ${({ align }) => `text-align: ${align}`};
  ${({ color }) => `color: ${color}`};
  font-family: ${({ theme }) => theme.font.fontFamily.skyclimbers};
`
const DynastySectionBioDescription = styled.p`
  width: 100%;
  font-size: ${({ theme }) => theme.font.fontSize.large};
  @media ${minWidthQueries.md} {
    font-size: ${({ theme }) => theme.font.fontSize.xLarge};
  }
  font-family: ${({ theme }) => theme.font.fontFamily.skyclimbers};
`
// #endregion

const Dynasty = ({ data: { dynasty, siteSettings, dynastiesList } }) => {
  const isMobile = useMediaQuery(maxWidthQueries.sm)
  const pageSeo = dynasty?.seo

  const sectionDividerImage = dynasty.divider?.formats?.medium?.url
    ? dynasty.divider?.formats?.medium?.url
    : dynasty.divider?.url
  const webmCover = dynasty.cover?.find(cov => cov.ext === '.webm')?.url
  const mp4Cover = dynasty.cover?.find(cov => cov.ext === '.mp4')?.url
  const imgCover = dynasty.cover?.find(
    cov => cov.ext === '.png' || cov.ext === '.jpg' || cov.ext === '.jpeg'
  )
  const ytParams = 'enablejsapi=1&controls=0&autoplay=1&loop=1'
  useEffect(() => {
    setTimeout(() => {
      document
        ?.getElementById('dynastyCoverVideo')
        ?.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*')
    }, 1000)
  }, [])

  let coverComponent

  if (dynasty.youtubeCover) {
    coverComponent = (
      <FullScreenSection
        background={{ backgroundColor: 'black' }}
        height={isMobile ? '50vh' : '75vh'}
        overflow="initial"
        wrap="wrap"
      >
        <iframe
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
          className="ytVid"
          frameBorder="0"
          height="100%"
          id="dynastyCoverVideo"
          src={`${dynasty.youtubeCover}${
            dynasty.youtubeCover.includes('?') ? '&' : '?'
          }${ytParams}`}
          title="YouTube video player"
          width="100%"
        />
        {sectionDividerImage && <SectionDivider src={sectionDividerImage} />}
      </FullScreenSection>
    )
  } else if (!dynasty.youtubeCover && webmCover && mp4Cover) {
    coverComponent = (
      <FullScreenSection height={isMobile ? '50vh' : '75vh'} overflow="initial" wrap="wrap">
        <FullScreenVideo height="100%" mp4={mp4Cover} webm={webmCover} />
        {sectionDividerImage && <SectionDivider src={sectionDividerImage} />}
      </FullScreenSection>
    )
  } else if (imgCover) {
    coverComponent = (
      <FullScreenSection
        background={`background-image: url(${imgCover?.url}); background-repeat: no-repeat; background-size: cover; background-position: center center;`}
        height={isMobile ? '50vh' : '75vh'}
        overflow="initial"
        wrap="wrap"
      >
        {sectionDividerImage && <SectionDivider src={sectionDividerImage} />}
      </FullScreenSection>
    )
  }
  return (
    <BasicLayout
      dynastiesList={dynastiesList}
      footerLinks={siteSettings?.footerLinks}
      footerLogo={siteSettings?.footerLogo?.url}
      headerLinks={siteSettings?.headerLinks}
      headerLogo={siteSettings?.logo?.url}
      socialLinks={siteSettings?.socialLinks}
    >
      <FullScreenSection overflow="initial" wrap="wrap">
        <PageSeo
          description={pageSeo?.description || undefined}
          image={pageSeo?.image?.formats?.large?.url || undefined}
          title={pageSeo?.title || `${dynasty?.name} Dynasty | Skyclimbers`}
        />
        {coverComponent && coverComponent}
      </FullScreenSection>
      {dynasty.pageSection &&
        dynasty.pageSection.length > 0 &&
        dynasty.pageSection?.map((section, index) => {
          const order = index % 2 === 0 ? index + 1 : index - 1
          const hasImage = section.media?.formats?.medium?.url
          let bioWidth = '50%'
          if (!hasImage) bioWidth = '70%'
          if (isMobile) bioWidth = '100%'
          const component = (
            <Container direction={isMobile ? 'column' : 'row'} height="100%">
              <Row justify={!hasImage && 'center'}>
                {hasImage && (
                  <DynastySectionMediaWrap
                    align="center"
                    height="100%"
                    justify="center"
                    order={isMobile ? 1 : index}
                    padding="1rem"
                    width={isMobile ? '100%' : '50%'}
                  >
                    <ProgressiveImage
                      placeholder={section.media?.formats?.thumbnail?.url}
                      src={section.media.formats.medium?.url}
                      style={{
                        height: section.media.formats.medium?.height,
                        width: section.media.formats.medium?.width,
                      }}
                    >
                      {(src, loading) => (
                        <DynastySectionMedia
                          alt={section.media.alternativeText}
                          src={src}
                          style={{ filter: loading ? 'blur(2px)' : '' }}
                        />
                      )}
                    </ProgressiveImage>
                  </DynastySectionMediaWrap>
                )}
                <DynastySectionBioWrap
                  align="center"
                  height="100%"
                  justify="flex-start"
                  order={isMobile ? 2 : order}
                  padding="1rem"
                  width={bioWidth}
                  wrap="wrap"
                >
                  <div>
                    <DynastySectionBioTitle
                      align={hasImage ? '' : 'center'}
                      color={dynasty.element.colors?.primary}
                    >
                      {section.title}
                    </DynastySectionBioTitle>
                    <DynastySectionBioDescription>
                      {section.description}
                      {section.link && (
                        <>
                          <br />
                          <br />
                        </>
                      )}
                    </DynastySectionBioDescription>
                    {section.link && (
                      <Link href={section.link}>
                        <a style={{ color: 'initial' }}>
                          <BlackButton margin="0">Explore {section.title}</BlackButton>
                        </a>
                      </Link>
                    )}
                  </div>
                </DynastySectionBioWrap>
              </Row>
            </Container>
          )
          return (
            <FullScreenSection
              key={section.id}
              direction="column"
              height="auto"
              overflow="initial"
              padding={isMobile ? '2rem 0' : '5rem 0'}
              wrap="wrap"
            >
              {component}
              {dynasty.pageSection.length - 1 !== index && (
                <SectionDivider src={sectionDividerImage} />
              )}
            </FullScreenSection>
          )
        })}
    </BasicLayout>
  )
}

export const getStaticPaths = async () => {
  const dynasties = await getDynasties()
  const paths =
    dynasties?.map(dynasty => {
      return { params: { slug: dynasty.slug || '' } }
    }) || []
  return {
    fallback: false,
    paths,
  }
}

export const getStaticProps = async ({ params }) => {
  const { slug } = params
  const dynasty = await getDynasties(`slug_eq=${slug}`)
  const siteSettings = await getSiteSettings()
  const dynastiesList = await getDynasties()

  return {
    props: {
      data: {
        dynastiesList: dynastiesList || null,
        dynasty: dynasty[0] || null,
        siteSettings: siteSettings || null,
      },
    },
    revalidate: false,
  }
}

export default Dynasty
